import React, { useCallback, useEffect, useRef, useState } from "react";
import FoodItem from "./FoodItem";
import Skeleton from "./Skeleton";
import AutoScrollCategories from "./AutoScrollCategories";
import LocationButton from "../Header/LocationButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchProducts } from "../../redux/menu/ProductSlice";
import { useNavigate } from "react-router-dom";
import { BsCart2 } from "react-icons/bs";
import { FaTimes, FaWhatsapp, FaBars } from "react-icons/fa";
import useTheme from "../../hooks/useTheme";
import { useTranslation } from "react-i18next";
import SubscriptionExpired from "../SubscriptionExpired/SubscriptionExpired";

// Add mobile-specific styles
const mobileStyles = `
  @media (max-width: 640px) {
    .category-division {
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      padding-left: 16px !important;
      padding-right: 16px !important;
      will-change: transform;
      transform: translateZ(0);
      backface-visibility: hidden;
      perspective: 1000px;
    }
    .category-division::-webkit-scrollbar {
      display: none;
    }
    .sticky-categories {
      top: 64px !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      z-index: 40 !important;
    }
  }
  @media (min-width: 641px) and (max-width: 768px) {
    .sticky-categories {
      top: 64px !important;
    }
  }
  @media (min-width: 769px) and (max-width: 1024px) {
    .sticky-categories {
      top: 88px !important;
    }
  }
  @media (min-width: 1025px) {
    .sticky-categories {
      top: 120px !important;
    }
  }
`;

const Foods = ({
  showCartButton = true,
  showAddToCart = true,
  highlightInfoIcon = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const menuReduxValue = useSelector((state) => state.menu);
  const { categories, status: categoriesStatus } = useSelector(
    (state) => state.category
  );
  const {
    products,
    status: productsStatus,
    isSearchMode,
    isOffersMode,
    selectedCategoryId,
    searchQuery,
  } = useSelector((state) => state.product);
  const { profile } = useSelector((state) => state.profile);
  const { banners, status: bannerStatus } = useSelector((state) => state.banner);
  const cartItems = useSelector((state) => state.cart.cart);

  // Sticky
  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef(null);
  
  // Check if banner exists to adjust spacing
  const bannerList = React.useMemo(() => banners?.banner || banners || [], [banners]);
  const hasBanner = (bannerStatus === 'succeeded' || bannerStatus === 'failed') && bannerList && bannerList.length > 0;

  const [menuTab, setMenuTab] = useState(menuReduxValue?.selectedCategory);
  const [currentCategoryId, setCurrentCategoryId] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Update whatsapp message when search query changes
  useEffect(() => {
    if (searchQuery) {
      setWhatsappMessage(t('common.looking_for_query', { query: searchQuery }));
    }
  }, [searchQuery, t]);

  // Set default message for offers mode
  useEffect(() => {
    if (isOffersMode) {
      setWhatsappMessage(t('common.looking_for_offer'));
    }
  }, [isOffersMode, t]);

  const isClosingFromPopState = useRef(false);

  // Handle browser back button for category modal
  useEffect(() => {
    if (showCategoryModal) {
      const modalId = 'category-modal';
      window.history.pushState({ modalId }, '');

      const handlePopState = () => {
        isClosingFromPopState.current = true;
        setShowCategoryModal(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (!isClosingFromPopState.current && window.history.state?.modalId === modalId) {
          window.history.back();
        }
        isClosingFromPopState.current = false;
      };
    }
  }, [showCategoryModal]);

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showCategoryModal) {
        setShowCategoryModal(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showCategoryModal]);

  const loadProducts = useCallback(
    (categoryId, page = 1) => {
      if (categoryId) {
        dispatch(fetchProducts({ categoryID: categoryId, page }));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (categories && categories.length > 0) {
      if (!menuTab && categories[0]) {
        setMenuTab(categories[0].name);
        setCurrentCategoryId(categories[0].id);
        loadProducts(categories[0].id, 1);
      }
    }
  }, [categories, menuTab, dispatch, loadProducts]);

  useEffect(() => {
    if (menuTab && categories) {
      const selectedCategory = categories.find((cat) => cat.name === menuTab);
      if (selectedCategory && selectedCategory.id !== currentCategoryId) {
        setCurrentCategoryId(selectedCategory.id);
        loadProducts(selectedCategory.id, 1);
      }
    }
  }, [menuTab, categories, currentCategoryId, loadProducts]);

  // Reset transition when products are loaded or failed, and start when loading
  useEffect(() => {
    if (productsStatus === "loading") {
      setIsTransitioning(true);
    } else if (productsStatus === "succeeded" || productsStatus === "failed") {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [productsStatus]);

  // Sync with Redux selectedCategoryId (when category is selected from search results)
  useEffect(() => {
    if (selectedCategoryId && categories) {
      const category = categories.find((cat) => cat.id === selectedCategoryId);
      if (category && category.name !== menuTab) {
        setMenuTab(category.name);
        setCurrentCategoryId(selectedCategoryId);
      }
    }
  }, [selectedCategoryId, categories, menuTab]);

  // Cart persistence is handled by localStorage, no need to reset on mount

  const handleMenuTabs = useCallback((type, index) => {
    // Don't re-select if the same category is already selected
    if (menuTab === type) {
      return;
    }
    setIsTransitioning(true);
    setMenuTab(type);
    // Products will be loaded automatically via useEffect that watches menuTab
  }, [menuTab]);

  // Sticky handler like plain JS
  useEffect(() => {
    const handleScroll = () => {
      if (!stickyRef.current) return;
      const stickyTop = stickyRef.current.offsetTop;
      setIsSticky(window.pageYOffset > stickyTop);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate total cart items
  const totalCartItems = cartItems
    ? cartItems.reduce((total, item) => total + (item.quantity || 0), 0)
    : 0;

  // Cart button refs and handlers
  const iconRef = useRef(null);
  const handleCartMouseEnter = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = isArabic
        ? "scaleX(-1) scale(1.1) rotate(-5deg)"
        : "scale(1.1) rotate(5deg)";
    }
  };
  const handleCartMouseLeave = () => {
    if (iconRef.current) {
      iconRef.current.style.transform = isArabic
        ? "scaleX(-1) scale(1) rotate(0deg)"
        : "scale(1) rotate(0deg)";
    }
  };

  return (
    <>
      <SubscriptionExpired />
      <style>{mobileStyles}</style>
      
      {/* Show location button when no banner (regardless of products) */}
      {!hasBanner && !isSearchMode && !isOffersMode && (
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 pt-16 md:pt-32 pb-0">
          <LocationButton />
        </div>
      )}
      
      <section
        className={`${
          isSticky ? "pt-4 md:pt-6" : ""
        } ${
          !hasBanner && categories && categories.length > 0 && !isSearchMode && !isOffersMode
            ? "pt-0 md:pt-0" 
            : !hasBanner && isOffersMode && products && products.length === 0
            ? "pt-20 md:pt-28" 
            : !hasBanner && isOffersMode
            ? "pt-20 md:pt-32"
            : !hasBanner && isSearchMode && products && products.length === 0
            ? "pt-0 md:pt-0"
            : !hasBanner && isSearchMode
            ? "pt-20 md:pt-32"
            : !hasBanner 
            ? "pt-0 md:pt-0" 
            : "md:my-12"
        } max-w-screen-xl mx-auto px-4 md:px-6 pb-8 md:pb-0`}
      >
        {/* Sticky Header - Only show when not in search mode and not in offers mode */}
        {!isSearchMode && !isOffersMode && (
          <div
            ref={stickyRef}
            className={`${
              isSticky
                ? "fixed left-0 right-0 z-50 bg-white shadow-md sticky-categories"
                : ""
            }`}
          >
            <AutoScrollCategories
              categories={categories}
              categoriesStatus={categoriesStatus}
              selectedCategory={menuTab}
              onCategorySelect={handleMenuTabs}
              onFilterClick={() => setShowCategoryModal(true)}
              isSticky={isSticky}
            />
          </div>
        )}


        {/* Category filter button for offers mode with no products */}
        {isOffersMode && products && products.length === 0 && (
          <div className={`flex justify-center mb-2 ${!hasBanner ? "mt-20 md:mt-28" : "mt-4"}`}>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Category filter button for search mode with products found */}
        {isSearchMode && products && products.length > 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Category filter button for offers mode with products found */}
        {isOffersMode && products && products.length > 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="cursor-pointer hover:scale-110 transition-transform"
              title="View All Categories"
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                style={{
                  border: `1px solid ${theme.colors.allCategoriesBorder}`,
                  backgroundColor: theme.colors.allCategoriesBg || "#ffffff",
                }}
              >
                <FaBars
                  className="w-6 h-6"
                  style={{ color: theme.colors.menuIconColor || theme.colors.primaryColor }}
                />
              </div>
            </button>
          </div>
        )}

        {/* Products Grid */}
        <div
          className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 lg:gap-8 ${
            !isSearchMode ? "mt-4 md:mt-8" : isSearchMode && products.length === 0 && !hasBanner ? "pt-0" : "pt-4"
          } min-h-[400px] transition-opacity duration-150 ${
            isTransitioning ? "opacity-40" : "opacity-100"
          } md:mb-12`}
        >
          {productsStatus === "loading" && products.length === 0 ? (
            Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} />
            ))
          ) : products.length === 0 ? (
            <div className="col-span-4 flex items-center justify-center py-4 min-h-[400px]">
              <div className="bg-white/70 backdrop-blur-md rounded-3xl shadow-2xl px-8 py-10 flex flex-col items-center max-w-md w-full border border-gray-100">
                  <svg
                    className="w-20 h-20 mb-6 text-primary/80"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="#f3f4f6"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-4.35-4.35"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M9.5 10.5c.5-1 2.5-1 3 0"
                      stroke="#f91944"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <circle cx="9" cy="9" r="1" fill="#f91944" />
                    <circle cx="13" cy="9" r="1" fill="#f91944" />
                  </svg>
                  <h2 className="text-2xl font-extrabold text-primary mb-2 poppins text-center">
                    {isOffersMode ? t('common.no_offers_found') : t('common.no_items_found')}
                  </h2>
                  <p className="text-gray-500 text-base text-center mt-2">
                    {isSearchMode 
                      ? t('common.no_search_results')
                      : isOffersMode
                      ? t('common.no_offers_available')
                      : t('common.no_category_items')}
                  </p>
                  
                  {/* WhatsApp Contact - Show in both search mode and empty categories */}
                  {profile?.whatsapp && (
                    <div className="w-full mt-6 space-y-3">
                      <p className="text-gray-700 text-sm text-center font-medium">
                        {isSearchMode ? t('common.cant_find_contact_whatsapp') : t('common.looking_for_something_contact')}
                      </p>
                      <textarea
                        value={whatsappMessage}
                        onChange={(e) => setWhatsappMessage(e.target.value)}
                        placeholder={isSearchMode ? t('common.enter_search_placeholder') : isOffersMode ? t('common.looking_for_offer') : t('common.tell_us_what_you_need')}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:border-primary transition-colors text-sm"
                        rows="3"
                      />
                      <button
                        onClick={() => {
                          if (whatsappMessage.trim()) {
                            // Construct professional message
                            let message = isSearchMode ? "🔍 *PRODUCT INQUIRY*\n\n" : "📦 *CATEGORY INQUIRY*\n\n";
                            message += "📋 *INQUIRY DETAILS*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `${isSearchMode ? 'Looking for' : 'Message'}: *${whatsappMessage}*\n`;
                            message += isSearchMode ? "Status: Item not found in online menu\n\n" : "Status: Category has no items\n\n";
                            
                            message += "❓ *QUESTIONS*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += "• Is this item available?\n";
                            message += "• What is the price?\n";
                            message += "• Can I place an order?\n\n";
                            
                            message += "🏪 *STORE INFORMATION*\n";
                            message += "━━━━━━━━━━━━━━━━━━━━\n";
                            message += `Store Name: ${profile.name || 'N/A'}\n`;
                            if (profile.location) {
                              message += `Location: ${profile.location}\n`;
                          }
                          if (profile.mobile) {
                              message += `Phone: ${profile.mobile}\n`;
                          }
                          if (profile.link) {
                              message += `Website: ${profile.link}\n`;
                          }
                          message += "\n";
                          
                          message += "⏰ *INQUIRY TIME*\n";
                          message += "━━━━━━━━━━━━━━━━━━━━\n";
                          message += `Date: ${new Date().toLocaleDateString()}\n`;
                          message += `Time: ${new Date().toLocaleTimeString()}\n\n`;
                          
                          message += "Thank you for your assistance! 🙏";
                          
                          const url = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
                          window.open(url, '_blank');
                        }
                      }}
                      className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <FaWhatsapp className="w-5 h-5" />
                      {t('common.connect_on_whatsapp')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            (() => {
              // Process products to swap highlighted items in even positions
              const processedProducts = [...products];

              for (let i = 0; i < processedProducts.length; i++) {
                const currentItem = processedProducts[i];
                const position = i + 1; // index + 1 for position

                // Check if current item is highlighted and in even position
                if (currentItem?.highlight && position % 2 === 0 && i > 0) {
                  const previousItem = processedProducts[i - 1];

                  // Check if previous item is not highlighted
                  if (!previousItem?.highlight) {
                    // Swap positions
                    processedProducts[i] = previousItem;
                    processedProducts[i - 1] = currentItem;
                  }
                }
              }

              return processedProducts.map((item) => {
                const currentCategory = categories?.find(
                  (cat) => cat.name === menuTab
                );
                return (
                  <FoodItem
                    key={item.id}
                    {...item}
                    currency={profile?.currency || "AED"}
                    image={item?.DocImage?.imageUrl}
                    is_veg={item?.is_veg}
                    categoryName={(isArabic && currentCategory?.name_ar) ? currentCategory.name_ar : currentCategory?.name}
                    categoryDescription={(isArabic && currentCategory?.description_ar) ? currentCategory.description_ar : currentCategory?.description}
                    showAddToCart={showAddToCart}
                    highlightInfoIcon={highlightInfoIcon}
                    highlight={item?.highlight || false}
                  />
                );
              });
            })()
          )}
        </div>

        {/* Cart Button with Count - Hidden on Mobile */}
        {showCartButton && profile?.enable_cart && (
          <div className={`fixed z-50 bottom-6 ${isArabic ? 'left-6' : 'right-6'} hidden md:block`}>
            <button
              className="rounded-full shadow-2xl w-20 h-20 flex items-center justify-center text-4xl border-2 border-opacity-90 transition-transform duration-300 hover:scale-110 relative"
              style={{
                background: `linear-gradient(to bottom right, ${theme.colors.cartIconGradientFrom}, ${theme.colors.cartIconGradientVia}, ${theme.colors.cartIconGradientTo})`,
                color: theme.colors.cartIconText,
                borderColor: theme.colors.cartIconBorder,
                borderWidth: "2.5px",
                boxShadow: theme.colors.cartIconShadow,
              }}
              aria-label="View Cart"
              onClick={() => navigate(`/${profile?.key}/orders`)}
              onMouseEnter={handleCartMouseEnter}
              onMouseLeave={handleCartMouseLeave}
            >
              <span
                ref={iconRef}
                className="inline-block text-5xl font-bold"
                style={{ transform: isArabic ? "scaleX(-1)" : undefined }}
              >
                <BsCart2 />
              </span>
              {totalCartItems > 0 && (
                <div
                  className={`text-xs font-bold rounded-full w-7 h-7 flex items-center justify-center z-10 border-2 shadow-lg absolute -top-1 ${isArabic ? '-left-1' : '-right-1'}`}
                  style={{
                    backgroundColor: theme.colors.cartIconBadgeBg,
                    color: theme.colors.cartIconBadgeText,
                    borderColor: theme.colors.cartIconBorder,
                  }}
                >
                  {totalCartItems}
                </div>
              )}
            </button>
          </div>
        )}

        {/* Category Selection Modal */}
        {showCategoryModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4 pb-32 md:pb-12"
            onClick={() => setShowCategoryModal(false)}
          >
            <div
              className="bg-white rounded-t-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
              style={{ backgroundColor: theme.colors.backgroundColor }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                className="flex items-center justify-between p-4 border-b"
                style={{ borderColor: theme.colors.borderColor }}
              >
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                  style={{ color: theme.colors.textSecondary }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: theme.colors.textPrimary }}
                >
                  {t('common.menu_categories')}
                </h3>
                <div className="w-8"></div> {/* Spacer for centering */}
              </div>

              {/* Modal Content - Vertical List */}
              <div
                className="overflow-y-auto flex-1"
                style={{
                  maxHeight: "60vh",
                  minHeight: "200px",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  scrollbarColor: `${theme.colors.borderColor} transparent`,
                }}
              >
                {categories &&
                  categories.map((categoryItem, index) => (
                    <button
                      key={categoryItem.id || index}
                      onClick={() => {
                        handleMenuTabs(categoryItem.name, index);
                        setShowCategoryModal(false);
                      }}
                      className={`w-full flex items-center justify-between py-4 px-4 hover:bg-gray-50 transition-colors ${isArabic ? 'border-r-4' : 'border-l-4'} min-h-[60px]`}
                      style={{
                        [isArabic ? 'borderRightColor' : 'borderLeftColor']:
                          menuTab === categoryItem.name
                            ? theme.colors.primaryColor
                            : "transparent",
                        backgroundColor:
                          menuTab === categoryItem.name
                            ? theme.colors.primaryColor + "05"
                            : "transparent",
                      }}
                    >
                      <div className={`flex items-center space-x-3 ${isArabic ? 'space-x-reverse' : ''}`}>
                        {categoryItem.DocImage?.imageUrl ? (
                          <img
                            src={categoryItem.DocImage.imageUrl}
                            alt={(isArabic && categoryItem.name_ar) ? categoryItem.name_ar : categoryItem.name}
                            className="w-6 h-6 object-contain rounded-full"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center">
                            <span className="text-lg">🍽️</span>
                          </div>
                        )}
                        <span
                          className="font-medium"
                          style={{
                            color: theme.colors.textPrimary,
                          }}
                        >
                          {(isArabic && categoryItem.name_ar) ? categoryItem.name_ar : categoryItem.name}
                        </span>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default Foods;
